import { useState } from "react";
import { toast } from "sonner";
import { TopBar } from "@/components/layout/TopBar";
import { PasswordInput } from "@/components/ui/PasswordInput";
import { useAuth, type AuthUser } from "@/lib/auth";
import api from "@/lib/api";
import { cn } from "@/lib/cn";

function apiError(err: unknown, fallback: string): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data
    ?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail) && detail.length && typeof detail[0]?.msg === "string") {
    return detail[0].msg;
  }
  return fallback;
}

const inputCls =
  "mt-1.5 w-full rounded-lg border border-line-strong px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-indigo-500";

export function Profile() {
  const { user, updateUser } = useAuth();
  const [name, setName] = useState(user?.display_name ?? "");
  const [saving, setSaving] = useState(false);

  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [changing, setChanging] = useState(false);

  if (!user) return null;

  const trimmed = name.trim();
  const dirty = trimmed.length > 0 && trimmed !== (user.display_name ?? "");

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!dirty) return;
    setSaving(true);
    try {
      const { data } = await api.patch<AuthUser>("/auth/me", { display_name: trimmed });
      updateUser(data);
      setName(data.display_name ?? "");
      toast.success("Profile updated");
    } catch (err) {
      toast.error(apiError(err, "Could not save your profile"));
    } finally {
      setSaving(false);
    }
  };

  const canChange = currentPw.length > 0 && newPw.length > 0 && confirmPw.length > 0;

  const changePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPw !== confirmPw) {
      toast.error("New passwords don't match.");
      return;
    }
    setChanging(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPw,
        new_password: newPw,
      });
      setCurrentPw("");
      setNewPw("");
      setConfirmPw("");
      toast.success("Password changed");
    } catch (err) {
      toast.error(apiError(err, "Could not change your password"));
    } finally {
      setChanging(false);
    }
  };

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <TopBar title="Profile" subtitle="Your account" />
      <div className="flex-1 overflow-y-auto bg-canvas">
        <div className="mx-auto max-w-lg space-y-6 px-6 py-6">
          <form onSubmit={save}>
            <div className="rounded-xl border border-line bg-surface p-6">
              <label className="block">
                <span className="text-sm font-medium text-ink-2">Display name</span>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={120}
                  placeholder="Your name"
                  className={inputCls}
                />
              </label>

              <div className="mt-5 grid grid-cols-2 gap-4 border-t border-line-soft pt-4 text-sm">
                <div>
                  <p className="text-xs uppercase tracking-wide text-ink-subtle">Email</p>
                  <p className="mt-0.5 text-ink-2">{user.email}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-ink-subtle">Role</p>
                  <p className="mt-0.5 capitalize text-ink-2">{user.role}</p>
                </div>
              </div>

              <div className="mt-5 flex justify-end">
                <button
                  type="submit"
                  disabled={!dirty || saving}
                  className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-indigo-700 disabled:opacity-50"
                >
                  {saving ? "Saving…" : "Save changes"}
                </button>
              </div>
            </div>
          </form>

          <form onSubmit={changePassword}>
            <div className="rounded-xl border border-line bg-surface p-6">
              <h2 className="text-sm font-semibold text-ink">Change password</h2>
              <div className="mt-3 space-y-3">
                <label className="block">
                  <span className="text-sm font-medium text-ink-2">
                    Current password
                  </span>
                  <PasswordInput
                    wrapperClassName="mt-1.5"
                    value={currentPw}
                    onChange={(e) => setCurrentPw(e.target.value)}
                    autoComplete="current-password"
                    className={cn(inputCls, "mt-0")}
                  />
                </label>
                <label className="block">
                  <span className="text-sm font-medium text-ink-2">New password</span>
                  <PasswordInput
                    wrapperClassName="mt-1.5"
                    value={newPw}
                    onChange={(e) => setNewPw(e.target.value)}
                    autoComplete="new-password"
                    className={cn(inputCls, "mt-0")}
                  />
                </label>
                <label className="block">
                  <span className="text-sm font-medium text-ink-2">
                    Confirm new password
                  </span>
                  <PasswordInput
                    wrapperClassName="mt-1.5"
                    value={confirmPw}
                    onChange={(e) => setConfirmPw(e.target.value)}
                    autoComplete="new-password"
                    className={cn(inputCls, "mt-0")}
                  />
                </label>
              </div>
              <div className="mt-4 flex justify-end">
                <button
                  type="submit"
                  disabled={!canChange || changing}
                  className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-indigo-700 disabled:opacity-50"
                >
                  {changing ? "Changing…" : "Change password"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
